import { supabase } from './supabase';

// Demo trading account — sim_accounts (one row per user) + sim_trades (an
// append-only ledger; RLS scopes both to the owner, anonymous guests included).
// All money here is virtual. Positions and average cost are derived from the
// ledger client-side (deterministic), so the ledger is the single source of
// truth; cash and realized P&L accumulate on the account row.

export const SIM_START_CASH = 100000;

export async function loadSimAccount(userId) {
  if (!supabase || !userId) return null;
  const { data, error } = await supabase
    .from('sim_accounts')
    .select('cash, realized_pnl, created_at')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) { console.error('[sim] account load →', error.code, error.message); return null; }
  if (data) return { cash: Number(data.cash), realizedPnl: Number(data.realized_pnl), createdAt: data.created_at };
  // First visit — open the demo account with the default virtual balance.
  const { error: insErr } = await supabase
    .from('sim_accounts')
    .insert({ user_id: userId, cash: SIM_START_CASH, realized_pnl: 0 });
  if (insErr && insErr.code !== '23505') { // 23505 = someone raced us; read below
    console.error('[sim] account create →', insErr.code, insErr.message);
    return null;
  }
  return { cash: SIM_START_CASH, realizedPnl: 0, createdAt: new Date().toISOString() };
}

export async function loadSimTrades(userId) {
  if (!supabase || !userId) return [];
  const { data, error } = await supabase
    .from('sim_trades')
    .select('id, symbol, side, qty, price, executed_at')
    .eq('user_id', userId)
    .order('executed_at', { ascending: true });
  if (error) { console.error('[sim] trades load →', error.code, error.message); return []; }
  return (data || []).map(t => ({
    id: t.id, symbol: t.symbol, side: t.side,
    qty: Number(t.qty), price: Number(t.price), executedAt: t.executed_at,
  }));
}

// Ledger → open positions with weighted-average cost. Sells reduce quantity at
// the running average (which is also how realized P&L is booked on execution).
export function computePositions(trades) {
  const bySym = {};
  for (const t of trades) {
    const p = bySym[t.symbol] || { qty: 0, cost: 0 };
    if (t.side === 'buy') {
      p.qty  += t.qty;
      p.cost += t.qty * t.price;
    } else {
      const avg = p.qty > 0 ? p.cost / p.qty : 0;
      p.qty  -= t.qty;
      p.cost -= t.qty * avg;
    }
    bySym[t.symbol] = p;
  }
  return Object.entries(bySym)
    .filter(([, p]) => p.qty > 1e-9)
    .map(([symbol, p]) => ({ symbol, qty: p.qty, avgCost: p.cost / p.qty }))
    .sort((a, b) => b.qty * b.avgCost - a.qty * a.avgCost);
}

// Execute a virtual trade. Validations mirror a real broker's basics:
// buy needs cash, sell needs held quantity. Returns { ok } or { error }.
export async function executeSimTrade(userId, { symbol, side, qty, price }, account, trades) {
  if (!supabase || !userId) return { error: 'אין חיבור לחשבון' };
  qty = Number(qty); price = Number(price);
  if (!(qty > 0) || !(price > 0)) return { error: 'כמות ומחיר חייבים להיות חיוביים' };
  const cost = qty * price;

  let cashDelta, realizedDelta = 0;
  if (side === 'buy') {
    if (cost > account.cash + 1e-6) return { error: 'אין מספיק מזומן וירטואלי לקנייה' };
    cashDelta = -cost;
  } else {
    const pos = computePositions(trades).find(p => p.symbol === symbol);
    if (!pos || pos.qty + 1e-9 < qty) return { error: 'אין מספיק יחידות למכירה' };
    cashDelta = cost;
    realizedDelta = (price - pos.avgCost) * qty;
  }

  const { error: tradeErr } = await supabase
    .from('sim_trades')
    .insert({ user_id: userId, symbol, side, qty, price });
  if (tradeErr) { console.error('[sim] trade insert →', tradeErr.code, tradeErr.message); return { error: 'שמירת הפעולה נכשלה' }; }

  const { error: accErr } = await supabase
    .from('sim_accounts')
    .update({
      cash: account.cash + cashDelta,
      realized_pnl: account.realizedPnl + realizedDelta,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId);
  if (accErr) {
    // The trade row is already in the ledger; surface the failure instead of
    // silently letting cash drift away from the ledger-derived positions.
    console.error('[sim] account update →', accErr.code, accErr.message);
    return { error: 'הפעולה נרשמה · עדכון היתרה נכשל, רענן את המסך' };
  }
  return { ok: true };
}

// Reset: wipe the ledger, restore the starting balance. Caller is responsible
// for the double confirmation — this just executes.
export async function resetSimAccount(userId) {
  if (!supabase || !userId) return { error: 'אין חיבור לחשבון' };
  const { error: delErr } = await supabase.from('sim_trades').delete().eq('user_id', userId);
  if (delErr) { console.error('[sim] reset delete →', delErr.code, delErr.message); return { error: 'מחיקת העסקאות נכשלה' }; }
  const { error: updErr } = await supabase
    .from('sim_accounts')
    .update({ cash: SIM_START_CASH, realized_pnl: 0, updated_at: new Date().toISOString() })
    .eq('user_id', userId);
  if (updErr) { console.error('[sim] reset update →', updErr.code, updErr.message); return { error: 'איפוס היתרה נכשל' }; }
  return { ok: true };
}
